import { Spinner } from '../../../shared/ui/index.js';
import type { AttentionAction, AttentionActionVariant } from '../lib/attention-actions.js';
import type {
  AttentionDetailTone,
  AttentionUrgency,
  AttentionView,
} from '../lib/attention-view.js';
import styles from './attention-row.module.css';

const ICON_SIZE = 20;

const URGENCY_CLASS: Record<AttentionUrgency, string | undefined> = {
  now: styles.now,
  soon: styles.soon,
  later: styles.later,
};

const DETAIL_CLASS: Record<AttentionDetailTone, string | undefined> = {
  danger: styles.detailDanger,
  muted: styles.detail,
};

const ACTION_CLASS: Record<AttentionActionVariant, string | undefined> = {
  primary: styles.actionPrimary,
  plain: styles.actionPlain,
  danger: styles.actionDanger,
};

type RowContentProps = {
  view: AttentionView;
};

const RowContent = ({ view }: RowContentProps) => (
  <>
    <span className={`${styles.icon} ${URGENCY_CLASS[view.urgency] ?? ''}`}>
      <view.icon size={ICON_SIZE} aria-hidden="true" />
    </span>
    <span className={styles.text}>
      <span className={styles.title}>{view.title}</span>
      {view.details.map((detail) => (
        <span key={detail.text} className={DETAIL_CLASS[detail.tone]}>
          {detail.text}
        </span>
      ))}
    </span>
    <span className={styles.chevron} aria-hidden="true" />
  </>
);

type ActionButtonProps = {
  action: AttentionAction;
  isBusy: boolean;
  disabled: boolean;
  onPress: (actionId: string) => void;
};

const ActionButton = ({ action, isBusy, disabled, onPress }: ActionButtonProps) => (
  <button
    type="button"
    className={`${styles.action} ${ACTION_CLASS[action.variant] ?? ''}`}
    disabled={disabled}
    aria-busy={isBusy}
    onClick={() => {
      onPress(action.id);
      action.onSelect();
    }}
  >
    {isBusy && <Spinner size="small" />}
    {action.label}
  </button>
);

type AttentionRowProps = {
  view: AttentionView;
  actions: AttentionAction[];
  busyActionId: string | null;
  isActing: boolean;
  onPress: (actionId: string) => void;
  onOpen: () => void;
};

export const AttentionRow = ({
  view,
  actions,
  busyActionId,
  isActing,
  onPress,
  onOpen,
}: AttentionRowProps) => (
  <li className={styles.item}>
    <button type="button" className={`${styles.main} ${styles.clickable}`} onClick={onOpen}>
      <RowContent view={view} />
    </button>
    <div className={styles.actions}>
      {actions.map((action) => (
        <ActionButton
          key={action.id}
          action={action}
          isBusy={isActing && busyActionId === action.id}
          disabled={isActing}
          onPress={onPress}
        />
      ))}
    </div>
  </li>
);
